/** Strip execution / output / snapshot fields from generation node fields.
 * Shared by duplicate-as-draft and regenerate-as-new fork. */

export const GENERATION_EXECUTION_STRIP_FIELD_KEYS = [
  'generatedOutputId',
  'generatedVideoUrl',
  'generatedTaskId',
  'generatedFromFingerprint',
  'generatedUpstreamFingerprint',
  'generatedConfigFingerprint',
  'needsRegeneration',
  // Virtual Try-On / shared media result pointers (regenerate-as-new must not inherit)
  'jobId',
  'outputAssetId',
  'previousOutputAssetId',
  'previousGeneratedOutputId',
  'generationHistory',
  'outputWidth',
  'outputHeight',
  'errorMessage',
  'resultInputFingerprint',
  'lastDispatchedPersonAssetId',
  'lastDispatchedGarmentAssetId',
  // VistoeraTASK-00512 — a fork / "另外生成" starts its OWN result group; it
  // must never inherit the origin's Result Slot group identity.
  'imageGenerationBatchId',
  'imageGenerationBatchSlotIndex',
  'imageGenerationBatchOriginNodeId',
  'imageGenerationBatchRequestedCount',
  'imageGenerationBatchFanOutCommitted',
  'imageGenerationBatchFollower',
  'imageGenerationBatchErrorSummary',
] as const

export function stripGenerationExecutionFields(fields: Record<string, unknown>): Record<string, unknown> {
  const next = { ...fields }
  for (const key of GENERATION_EXECUTION_STRIP_FIELD_KEYS) {
    delete next[key]
  }
  return next
}

export function resetGenerationStatusToDraft(fields: Record<string, unknown>): Record<string, unknown> {
  return { ...stripGenerationExecutionFields(fields), status: 'READY' }
}

/**
 * TASK-00225 — Duplicate = editable SNAPSHOT clone, never a new
 * generation run. Unlike `resetGenerationStatusToDraft` above (used by
 * Diverge/"regenerate-as-new-fork", which correctly wipes every result
 * field because a fork legitimately starts with NO output yet),
 * Duplicate must show the ORIGIN node's CURRENT result immediately —
 * blindly reusing `stripGenerationExecutionFields` here was the root
 * cause of Duplicate's "spinner"/empty-result bug: it deleted
 * `generatedOutputId` / `generatedVideoUrl` / `outputAssetId` /
 * `generationHistory` / etc. (the actual visible result) and then
 * forced `status` to `'READY'` unconditionally, so a completed node's
 * result vanished after Duplicate.
 *
 * Only genuine ACTIVE-run-ownership / transient-failure fields are
 * stripped here — never the result identity itself. `status` is then
 * re-derived from what's ACTUALLY still on the node (never forced),
 * so a completed source shows its result immediately, a
 * generating/failed source falls back to its last stable result if
 * any, and only a source with no result at all becomes an idle,
 * ready-to-generate draft.
 */
export const GENERATION_ACTIVE_OWNERSHIP_STRIP_FIELD_KEYS = [
  // Virtual Try-On / Motion Reconstruction — the field that actually
  // drives mount-time run hydration/polling for THESE node types (via
  // their own `fields.jobId`, independent of the top-level
  // `FlowNodeData.latestRunId` video/image nodes key off — see
  // `GenerateTryOnNode.tsx`'s own `runHydrating` init). Never the
  // result itself (`outputAssetId`/`generatedOutputId` cover that).
  'jobId',
  // Transient failure text — a duplicate must never immediately show
  // the ORIGIN's last error message as its own.
  'errorMessage',
  // Try-On dispatch de-dup bookkeeping tied to ONE specific prior
  // dispatch attempt — must not make the new node believe it already
  // dispatched for the same person/garment pair.
  'lastDispatchedPersonAssetId',
  'lastDispatchedGarmentAssetId',
  // VistoeraTASK-00512 — a duplicate is a draft clone, never a member of the
  // origin's Result Slot group (otherwise "regenerate" would hijack its slots).
  'imageGenerationBatchId',
  'imageGenerationBatchSlotIndex',
  'imageGenerationBatchOriginNodeId',
  'imageGenerationBatchRequestedCount',
  'imageGenerationBatchFanOutCommitted',
  'imageGenerationBatchFollower',
  'imageGenerationBatchErrorSummary',
] as const

export function stripActiveRunOwnershipFields(fields: Record<string, unknown>): Record<string, unknown> {
  const next = { ...fields }
  for (const key of GENERATION_ACTIVE_OWNERSHIP_STRIP_FIELD_KEYS) {
    delete next[key]
  }
  return next
}

/** Result-identity field names spanning every generation node type this
 * module is shared by. Only ever used to answer "does this node
 * already have SOMETHING visible to show" for `status` re-derivation
 * below — never to pick which field a particular node type actually
 * renders (each node component still owns that itself). */
const RESULT_IDENTITY_FIELD_KEYS = [
  'generatedVideoUrl',
  'generatedOutputId',
  'outputAssetId',
  'previousOutputId',
  'previousOutputAssetId',
  'previousGeneratedOutputId',
  'audioAssetId',
  'previewAudioAssetId',
  'previousAudioAssetId',
  'previousPreviewAudioAssetId',
] as const

function isMeaningfulResultValue(value: unknown): boolean {
  if (typeof value === 'string') return value.trim() !== ''
  return Boolean(value)
}

function hasAnyResultIdentity(fields: Record<string, unknown>): boolean {
  return RESULT_IDENTITY_FIELD_KEYS.some((key) => isMeaningfulResultValue(fields[key]))
}

export function buildDuplicateSnapshotFields(fields: Record<string, unknown>): Record<string, unknown> {
  const stripped = stripActiveRunOwnershipFields(fields)
  const currentStatus = typeof fields.status === 'string' ? fields.status : undefined
  const status = currentStatus === 'COMPLETED' || hasAnyResultIdentity(stripped) ? 'COMPLETED' : 'READY'
  return { ...stripped, status }
}
